import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Recap under the add to cart button: selected size, "free shipping" badge, and the total
 * (product + selected vases) shown inside the add to cart button.
 *
 * Stateless: everything is read back from the DOM, so it stays correct after the native variant
 * picker and product form morph their markup. It re-renders on the native ProductSelectEvent
 * (instantly, then once the section has been re-rendered) and on vase changes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} selection
 * @property {HTMLElement} [shipping]
 *
 * @extends {Component<Refs>}
 */
class ProductSelectionSummary extends Component {
  requiredRefs = ['selection'];

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.#scope.addEventListener('change', this.#onChange, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {Element | Document} */
  get #scope() {
    return this.closest('.shopify-section, dialog') ?? document;
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    this.#render();
    event.promise
      // The product form morphs the add to cart button (and its total) with the server markup once
      // this promise resolves: render on the next frame so the vase amount is added back afterwards.
      ?.then(() => requestAnimationFrame(() => this.#render()))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-selection-summary] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.matches('[data-additional-item-variant-id]')) this.#render();
  };

  get #variantPicker() {
    const { productId } = this.dataset;
    return (
      this.#scope.querySelector(`.product-size-selector[data-product-id="${productId}"]`) ??
      this.#scope.querySelector(`variant-picker[data-product-id="${productId}"]`)
    );
  }

  /** @returns {HTMLElement | null} */
  get #totalElement() {
    return (this.closest('product-form-component') ?? this.#scope).querySelector('[data-product-cta-total]');
  }

  #render() {
    const picker = this.#variantPicker;

    /** @type {HTMLInputElement | null | undefined} */
    const sizeInput = picker?.querySelector('fieldset[data-size-option="true"] input:checked');
    /** @type {HTMLInputElement | null | undefined} */
    const pricedInput = sizeInput?.dataset.price ? sizeInput : picker?.querySelector('input[data-price]:checked');

    const variant = this.#readVariantJson(picker);

    const productPrice = Number(pricedInput?.dataset.price ?? variant?.price);
    const label =
      sizeInput?.dataset.shortLabel ||
      Array.from(picker?.querySelectorAll('fieldset input:checked') ?? [])
        .map((input) => /** @type {HTMLInputElement} */ (input).dataset.shortLabel)
        .filter(Boolean)
        .join(' / ') ||
      variant?.title ||
      '';

    /** @type {NodeListOf<HTMLInputElement>} */
    const vaseInputs = this.#scope.querySelectorAll('input[data-additional-item-variant-id]:checked:not(:disabled)');
    const vases = Array.from(vaseInputs);

    const { selectionPrefix = '' } = this.dataset;
    const selection = [`${selectionPrefix} ${label}`.trim(), ...vases.map((input) => input.dataset.title)]
      .filter(Boolean)
      .join(' + ');
    this.refs.selection.textContent = selection;

    if (Number.isNaN(productPrice)) return;

    const total = vases.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), productPrice);

    const totalElement = this.#totalElement;
    if (totalElement) totalElement.textContent = this.#formatMoney(total);

    const threshold = Number(this.dataset.freeShippingThreshold) || 0;
    this.refs.shipping?.setAttribute('data-free-shipping', String(total >= threshold));
  }

  /**
   * @param {Element | null} picker
   * @returns {{ price?: number, title?: string } | null}
   */
  #readVariantJson(picker) {
    const textContent = picker?.querySelector('script[type="application/json"]')?.textContent;
    if (!textContent) return null;

    try {
      return JSON.parse(textContent);
    } catch {
      return null;
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'fr', {
      style: 'currency',
      currency: this.dataset.currency || 'EUR',
    }).format(cents / 100);
  }
}

if (!customElements.get('product-selection-summary')) {
  customElements.define('product-selection-summary', ProductSelectionSummary);
}
